import type { PatientContactModel } from './types';
import { DataTable } from '~/shared/data-tables/DataTable';
import { deletePatientContactOptions, getPatientContactsOptions, type DeletePatientContactCommand } from '@generated';
import { useMutation } from '~/util/http/useMutation';
import { ContactFormModal } from './forms/ContactFormModal';
import type { DataTableColumnProps } from '~/shared/data-tables/DataTableColumn';
import { TableActions } from '~/shared/data-tables/TableActions';

function RenderActionsCell(props: PatientContactModel) {
	const deleteContact = useMutation<void, DeletePatientContactCommand>({
		...deletePatientContactOptions,
		invalidateQueryKeys: [getPatientContactsOptions(props.stayId).queryKey]
	});
	const onRemove = () => {
		if (confirm('Are you sure you want to remove this contact?')) {
			deleteContact.mutate({ stayId: props.stayId, patientContactId: props.id });
		}
	};
	return (
		<TableActions>
			<TableActions.ViewButton
				renderModal={closeModal => <ContactFormModal stayId={props.stayId} existingContact={props} closeModal={closeModal} />}
			/>
			<TableActions.DeleteButton onClick={onRemove} isPending={deleteContact.isPending} />
		</TableActions>
	);
}

const columns: DataTableColumnProps<PatientContactModel>[] = [
	{
		header: 'Contact',
		key: 'name',
		renderCell: d => `${d.lastName}, ${d.firstName}`
	},
	{
		header: 'Primary Contact',
		key: 'primaryContact',
		renderCell: d => (d.primaryContact ? 'Yes' : 'No')
	},
	{
		header: 'Emergency Contact',
		key: 'emergencyContact',
		renderCell: d => (d.emergencyContact ? 'Yes' : 'No')
	},
	{
		header: 'Caregiver',
		key: 'caregiver',
		renderCell: d => (d.caregiver ? 'Yes' : 'No')
	},
	{
		header: 'Relationship',
		key: 'relationshipLabel'
	},
	{
		key: 'actions',
		header: 'Actions',
		width: 'w-24',
		renderCell: d => <RenderActionsCell {...d} />
	}
];

export function PatientContactsTable({ rows }: { rows: PatientContactModel[] }) {
	return (
		<DataTable
			columns={columns}
			rows={rows}
			viewMore={{
				text: 'View Additional Contacts',
				threshold: 3
			}}
		/>
	);
}
